import React, { useState } from 'react';
import { 
  FaBed, 
  FaWifi, 
  FaSnowflake, 
  FaTv, 
  FaShower, 
  FaWineBottle,
  FaLock,
  FaTshirt,
  FaStar,
  FaUtensils,
  FaBell,
  FaCar,
  FaBriefcase,
  FaMapMarkedAlt,
  FaCreditCard,
  FaPhone,
  FaClock,
  FaShieldAlt,
  FaCalendarAlt,
  FaUser,
  FaEnvelope,
  FaHome,
  FaUsers,
  FaChild,
  FaCommentAlt,
  FaCheckCircle,
  FaRupeeSign,
  FaMapMarkerAlt,
  FaCrown,
  FaConciergeBell,
  FaParking,
  FaUserTie,
  FaBinoculars
} from 'react-icons/fa';
import { 
  MdKingBed,
  MdAcUnit,
  MdLocalLaundryService,
  MdRoomService,
  MdLocationOn,
  MdEventAvailable,
  MdPayment,
  MdSupportAgent
} from 'react-icons/md';
import { 
  GiHotMeal,
  GiPartyPopper
} from 'react-icons/gi';
import { 
  IoIosPeople,
  IoMdCalendar
} from 'react-icons/io';

const Appointment = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    roomType: '',
    checkIn: '',
    checkOut: '',
    adults: '1',
    children: '0',
    specialRequests: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Room types data
  const roomTypes = [
    {
      id: 'deluxe',
      name: 'Deluxe Room',
      description: 'Spacious room with modern amenities',
      price: '₹3,500/night',
      size: '350 sq ft',
      capacity: '2 Adults + 1 Child',
      icon: <MdKingBed className="text-amber-600" />
    },
    {
      id: 'super-deluxe',
      name: 'Super Deluxe',
      description: 'Luxury room with river view',
      price: '₹4,500/night',
      size: '400 sq ft',
      capacity: '3 Adults',
      icon: <FaCrown className="text-amber-600" />
    },
    {
      id: 'executive',
      name: 'Executive Suite',
      description: 'Premium suite with separate living area',
      price: '₹6,500/night',
      size: '550 sq ft',
      capacity: '2 Adults + 2 Children',
      icon: <FaUserTie className="text-amber-600" />
    },
    {
      id: 'family',
      name: 'Family Suite',
      description: 'Perfect for family stays',
      price: '₹8,000/night',
      size: '650 sq ft',
      capacity: '4 Adults',
      icon: <IoIosPeople className="text-amber-600" />
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.roomType) newErrors.roomType = 'Please select a room type';
    if (!formData.checkIn) newErrors.checkIn = 'Check-in date is required';
    if (!formData.checkOut) newErrors.checkOut = 'Check-out date is required';
    
    // Validate dates
    if (formData.checkIn && formData.checkOut) {
      const checkInDate = new Date(formData.checkIn);
      const checkOutDate = new Date(formData.checkOut);
      if (checkOutDate <= checkInDate) {
        newErrors.checkOut = 'Check-out date must be after check-in date';
      }
    }
    
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        roomType: '',
        checkIn: '',
        checkOut: '',
        adults: '1',
        children: '0',
        specialRequests: ''
      });
      
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    }, 1500);
  };

  const calculateNights = () => {
    if (formData.checkIn && formData.checkOut) {
      const checkIn = new Date(formData.checkIn);
      const checkOut = new Date(formData.checkOut);
      const diffTime = Math.abs(checkOut - checkIn);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
    return 0;
  };

  const calculateTotal = () => {
    const nights = calculateNights();
    if (!nights || !formData.roomType) return 0;
    
    const room = roomTypes.find(r => r.id === formData.roomType);
    if (!room) return 0;
    
    const price = parseInt(room.price.replace(/[^0-9]/g, ''));
    return price * nights;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-red-800 via-red-700 to-red-600 text-white">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 flex items-center">
              <FaBed className="mr-4 text-amber-300" />
              Room Booking
            </h1>
            <p className="text-xl md:text-2xl text-amber-100 mb-8 max-w-3xl">
              Book A Luxury Room at Hotel Prakash Palace - Experience Unmatched Comfort in Varanasi
            </p>
            
            {/* Booking Stats with Icons */}
            {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
                <div className="flex items-center justify-center mb-2">
                  <MdSupportAgent className="w-8 h-8 text-amber-300" />
                </div>
                <div className="text-3xl font-bold mb-2">24/7</div>
                <div className="text-amber-200">Support</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
                <div className="flex items-center justify-center mb-2">
                  <FaStar className="w-8 h-8 text-amber-300" />
                </div>
                <div className="text-3xl font-bold mb-2">98%</div>
                <div className="text-amber-200">Guest Satisfaction</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
                <div className="flex items-center justify-center mb-2">
                  <FaBed className="w-8 h-8 text-amber-300" />
                </div>
                <div className="text-3xl font-bold mb-2">50+</div>
                <div className="text-amber-200">Luxury Rooms</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
                <div className="flex items-center justify-center mb-2">
                  <FaConciergeBell className="w-8 h-8 text-amber-300" />
                </div>
                <div className="text-3xl font-bold mb-2">5★</div>
                <div className="text-amber-200">Service Rating</div>
              </div>
            </div> */}
          </div>
        </div>
        
        {/* Wave Design */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative w-full h-16">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" 
            className="fill-amber-50"></path>
          </svg>
        </div>
      </div>

      <main className="container mx-auto px-4 py-12 md:py-16">
        {/* Success Message */}
        {isSubmitted && (
          <div className="mb-8 max-w-4xl mx-auto bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg shadow-lg animate-fadeIn">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FaCheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-green-800">Booking Request Sent Successfully!</h3>
                <p className="mt-2 text-green-700">
                  Thank you for choosing Hotel Prakash Palace. We'll confirm your booking within 2 hours. 
                  Booking reference: <span className="font-bold">HPP-{Date.now().toString().slice(-6)}</span>
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Room Gallery */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {/* Room Images Gallery */}
              <div className="relative h-64 md:h-96">
                <img 
                  src="https://www.hotelprakashpalace.in/img/gallery/room5.jpeg" 
                  alt="Deluxe Room - Hotel Prakash Palace"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://images.unsplash.com/photo-1611892440504-42a792e24d32?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
                  }}
                />
                
              </div>
              
              {/* Thumbnail Gallery */}
              <div className="grid grid-cols-3 gap-2 p-4">
                <div className="relative h-32 rounded-lg overflow-hidden">
                  <img 
                    src="https://www.hotelprakashpalace.in/img/about-3.jpg" 
                    alt="Hotel View"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="relative h-32 rounded-lg overflow-hidden">
                  <img 
                    src="https://www.hotelprakashpalace.in/img/gallery/room1.jpeg" 
                    alt="Executive Room"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="relative h-32 rounded-lg overflow-hidden bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center">
                  <div className="text-center">
                    <FaBed className="w-8 h-8 text-red-600 mx-auto mb-2" />
                    <span className="text-sm font-medium text-amber-700">View More Photos</span>
                  </div>
                </div>
              </div>

              {/* Room Features with Icons */}
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                  <FaStar className="mr-2 text-amber-600" />
                  Why Choose Our Rooms?
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { icon: <FaBed className="w-6 h-6" />, title: 'King Size Bed', desc: 'Premium comfort' },
                    { icon: <FaWifi className="w-6 h-6" />, title: 'Free WiFi', desc: 'High speed internet' },
                    { icon: <MdAcUnit className="w-6 h-6" />, title: 'AC Rooms', desc: 'Climate control' },
                    { icon: <FaTv className="w-6 h-6" />, title: 'LED TV', desc: 'Entertainment' },
                    { icon: <FaShower className="w-6 h-6" />, title: 'Hot Water', desc: '24/7 available' },
                    { icon: <FaWineBottle className="w-6 h-6" />, title: 'Mini Bar', desc: 'Refreshments' },
                    { icon: <FaLock className="w-6 h-6" />, title: 'Safe Locker', desc: 'Secure storage' },
                    { icon: <MdLocalLaundryService className="w-6 h-6" />, title: 'Laundry', desc: 'Daily service' }
                  ].map((feature, index) => (
                    <div key={index} className="bg-amber-50 p-4 rounded-lg text-center hover:bg-amber-100 transition duration-300">
                      <div className="text-amber-600 mb-2 flex justify-center">
                        {feature.icon}
                      </div>
                      <h4 className="font-semibold text-gray-800">{feature.title}</h4>
                      <p className="text-sm text-gray-600">{feature.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Room Types with Icons */}
            <div className="mt-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                <MdKingBed className="mr-2 text-amber-600" />
                Available Room Types
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {roomTypes.map((room) => (
                  <div key={room.id} className={`bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition duration-300 ${formData.roomType === room.id ? 'ring-2 ring-amber-500' : ''}`}>
                    <div className="p-6">
                      <div className="flex items-start mb-4">
                        <div className="bg-amber-100 p-3 rounded-lg mr-4">
                          {room.icon}
                        </div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-gray-800">{room.name}</h4>
                          <p className="text-amber-600 font-bold mt-1 flex items-center">
                            <FaRupeeSign className="mr-1" />
                            {room.price}
                          </p>
                        </div>
                        <div className={`px-3 py-1 rounded-full ${formData.roomType === room.id ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-800'}`}>
                          {room.size}
                        </div>
                      </div>
                      <p className="text-gray-600 mb-4">{room.description}</p>
                      <div className="flex justify-between items-center text-sm text-gray-500">
                        <span className="flex items-center">
                          <FaUsers className="mr-1" />
                          {room.capacity}
                        </span>
                        <button
                          onClick={() => setFormData(prev => ({ ...prev, roomType: room.id }))}
                          className={`px-4 py-2 rounded-lg transition duration-300 flex items-center ${formData.roomType === room.id ? 'bg-amber-600 text-white' : 'bg-gray-100 hover:bg-amber-100 text-gray-800'}`}
                        >
                          {formData.roomType === room.id ? (
                            <>
                              <FaCheckCircle className="mr-2" />
                              Selected
                            </>
                          ) : (
                            'Select Room'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Booking Form with Icons */}
          <div>
            <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-6">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-2 flex items-center">
                  <IoMdCalendar className="mr-2 text-amber-600" />
                  Book Your Stay
                </h2>
                <p className="text-gray-600">Fill in the details to reserve your luxury room</p>
              </div>

              {/* Booking Summary */}
              {formData.roomType && (
                <div className="mb-6 bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-4">
                  <h4 className="font-bold text-gray-800 mb-2 flex items-center">
                    <FaCalendarAlt className="mr-2 text-amber-600" />
                    Booking Summary
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 flex items-center">
                        <FaBed className="mr-2" />
                        Room Type:
                      </span>
                      <span className="font-semibold">
                        {roomTypes.find(r => r.id === formData.roomType)?.name}
                      </span>
                    </div>
                    {formData.checkIn && formData.checkOut && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 flex items-center">
                            <FaClock className="mr-2" />
                            Duration:
                          </span>
                          <span className="font-semibold">{calculateNights()} nights</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 flex items-center">
                            <FaRupeeSign className="mr-2" />
                            Total:
                          </span>
                          <span className="font-bold text-amber-700 text-lg flex items-center">
                            <FaRupeeSign className="mr-1" />
                            {calculateTotal().toLocaleString()}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <FaUser className="mr-2 text-amber-600" />
                    Your Name <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition duration-300`}
                      placeholder="Enter your full name"
                    />
                    <FaUser className="absolute left-3 top-3.5 text-gray-400" />
                  </div>
                  {errors.name && <p className="mt-1 text-sm text-red-600 flex items-center">
                    <FaUser className="mr-1" /> {errors.name}
                  </p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <FaEnvelope className="mr-2 text-amber-600" />
                    Your Email <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition duration-300`}
                      placeholder="Enter your email"
                    />
                    <FaEnvelope className="absolute left-3 top-3.5 text-gray-400" />
                  </div>
                  {errors.email && <p className="mt-1 text-sm text-red-600 flex items-center">
                    <FaEnvelope className="mr-1" /> {errors.email}
                  </p>}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <FaPhone className="mr-2 text-amber-600" />
                    Phone Number <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition duration-300`}
                      placeholder="Enter your phone number"
                    />
                    <FaPhone className="absolute left-3 top-3.5 text-gray-400" />
                  </div>
                  {errors.phone && <p className="mt-1 text-sm text-red-600 flex items-center">
                    <FaPhone className="mr-1" /> {errors.phone}
                  </p>}
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <FaHome className="mr-2 text-amber-600" />
                    Address
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition duration-300"
                      placeholder="Enter your address"
                    />
                    <FaHome className="absolute left-3 top-3.5 text-gray-400" />
                  </div>
                </div>

                {/* Room Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <FaBed className="mr-2 text-amber-600" />
                    Select A Room <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="roomType"
                      value={formData.roomType}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 border ${errors.roomType ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition duration-300 bg-white appearance-none`}
                    >
                      <option value="">Select a room type</option>
                      {roomTypes.map(room => (
                        <option key={room.id} value={room.id}>
                          {room.name} - {room.price}
                        </option>
                      ))}
                    </select>
                    <FaBed className="absolute left-3 top-3.5 text-gray-400" />
                  </div>
                  {errors.roomType && <p className="mt-1 text-sm text-red-600 flex items-center">
                    <FaBed className="mr-1" /> {errors.roomType}
                  </p>}
                </div>

                {/* Check-in Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <FaCalendarAlt className="mr-2 text-amber-600" />
                    Check In Date <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="checkIn"
                      value={formData.checkIn}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className={`w-full pl-10 pr-4 py-3 border ${errors.checkIn ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition duration-300`}
                    />
                    <FaCalendarAlt className="absolute left-3 top-3.5 text-gray-400" />
                  </div>
                  {errors.checkIn && <p className="mt-1 text-sm text-red-600 flex items-center">
                    <FaCalendarAlt className="mr-1" /> {errors.checkIn}
                  </p>}
                </div>

                {/* Check-out Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <FaCalendarAlt className="mr-2 text-amber-600" />
                    Check Out Date <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="checkOut"
                      value={formData.checkOut}
                      onChange={handleChange}
                      min={formData.checkIn || new Date().toISOString().split('T')[0]}
                      className={`w-full pl-10 pr-4 py-3 border ${errors.checkOut ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition duration-300`}
                    />
                    <FaCalendarAlt className="absolute left-3 top-3.5 text-gray-400" />
                  </div>
                  {errors.checkOut && <p className="mt-1 text-sm text-red-600 flex items-center">
                    <FaCalendarAlt className="mr-1" /> {errors.checkOut}
                  </p>}
                </div>

                {/* Guests */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FaUsers className="mr-2 text-amber-600" />
                      Adults
                    </label>
                    <div className="relative">
                      <select
                        name="adults"
                        value={formData.adults}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition duration-300 bg-white appearance-none"
                      >
                        {[1,2,3,4,5,6].map(num => (
                          <option key={num} value={num}>{num} Adult{num > 1 ? 's' : ''}</option>
                        ))}
                      </select>
                      <FaUsers className="absolute left-3 top-3.5 text-gray-400" />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <FaChild className="mr-2 text-amber-600" />
                      Children
                    </label>
                    <div className="relative">
                      <select
                        name="children"
                        value={formData.children}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition duration-300 bg-white appearance-none"
                      >
                        {[0,1,2,3,4].map(num => (
                          <option key={num} value={num}>{num} Child{num !== 1 ? 'ren' : ''}</option>
                        ))}
                      </select>
                      <FaChild className="absolute left-3 top-3.5 text-gray-400" />
                    </div>
                  </div>
                </div>

                {/* Special Requests */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <FaCommentAlt className="mr-2 text-amber-600" />
                    Special Requests
                  </label>
                  <div className="relative">
                    <textarea
                      name="specialRequests"
                      value={formData.specialRequests}
                      onChange={handleChange}
                      rows="3"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition duration-300 resize-none"
                      placeholder="Any special requirements or requests..."
                    />
                    <FaCommentAlt className="absolute left-3 top-3 text-gray-400" />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-bold py-4 px-6 rounded-lg text-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl flex items-center justify-center ${isLoading ? 'opacity-90 cursor-not-allowed' : ''}`}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing Booking...
                    </>
                  ) : (
                    <>
                      <FaCheckCircle className="w-6 h-6 mr-3" />
                      Book Now - ₹{calculateTotal().toLocaleString() || '0'}
                    </>
                  )}
                </button>

                {/* Booking Policies with Icons */}
                <div className="text-center text-sm text-gray-500 mt-4 space-y-2">
                  <p className="flex items-center justify-center">
                    <FaCheckCircle className="mr-2 text-green-500" />
                    Free cancellation 48 hours before check-in
                  </p>
                  <p className="flex items-center justify-center">
                    <FaRupeeSign className="mr-2 text-green-500" />
                    Best price guaranteed
                  </p>
                  <p className="flex items-center justify-center">
                    <FaShieldAlt className="mr-2 text-green-500" />
                    No hidden charges
                  </p>
                </div>
              </form>
            </div>

            {/* Contact Info with Icons */}
            <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
              <h4 className="font-bold text-gray-800 mb-4 flex items-center">
                <FaPhone className="mr-2 text-amber-600" />
                Need Help?
              </h4>
              <div className="space-y-3">
                <div className="flex items-center">
                  <FaPhone className="w-5 h-5 text-amber-600 mr-3" />
                  <span className="text-gray-700">+91 9119641118</span>
                </div>
                <div className="flex items-center">
                  <FaClock className="w-5 h-5 text-amber-600 mr-3" />
                  <span className="text-gray-700">24/7 Support</span>
                </div>
                <div className="flex items-center">
                  <FaShieldAlt className="w-5 h-5 text-amber-600 mr-3" />
                  <span className="text-gray-700">Secure Payment</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hotel Highlights with Icons */}
        {/* <div className="mt-16 max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-amber-800 to-amber-700 text-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-8 md:p-12">
              <h3 className="text-3xl font-bold mb-8 text-center flex items-center justify-center">
                <FaStar className="mr-2 text-amber-300" />
                Hotel Prakash Palace Highlights
                <FaStar className="ml-2 text-amber-300" />
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {[
                  { icon: <FaMapMarkerAlt className="w-8 h-8" />, title: 'Prime Location', desc: 'Near BHU & Assi Ghat' },
                  { icon: <FaStar className="w-8 h-8" />, title: '5-Star Service', desc: 'Luxury hospitality' },
                  { icon: <GiHotMeal className="w-8 h-8" />, title: 'Multi-Cuisine', desc: 'Fine dining restaurant' },
                  { icon: <FaBell className="w-8 h-8" />, title: '24/7 Service', desc: 'Round the clock service' },
                  { icon: <FaCar className="w-8 h-8" />, title: 'Free Parking', desc: 'Secure parking space' },
                  { icon: <FaBriefcase className="w-8 h-8" />, title: 'Business Center', desc: 'Meeting facilities' },
                  { icon: <FaBinoculars className="w-8 h-8" />, title: 'Tour Assistance', desc: 'Local sightseeing help' },
                  { icon: <FaCreditCard className="w-8 h-8" />, title: 'Easy Payment', desc: 'Multiple payment options' }
                ].map((highlight, index) => (
                  <div key={index} className="text-center group hover:transform hover:-translate-y-1 transition duration-300">
                    <div className="text-4xl mb-4 flex justify-center">
                      <div className="bg-amber-900/30 p-4 rounded-full group-hover:bg-amber-900/50 transition duration-300">
                        {highlight.icon}
                      </div>
                    </div>
                    <h4 className="text-xl font-bold mb-2">{highlight.title}</h4>
                    <p className="text-amber-100">{highlight.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div> */}
      </main>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
        input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(0.5);
        }
        select {
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
          background-position: right 0.5rem center;
          background-repeat: no-repeat;
          background-size: 1.5em 1.5em;
          padding-right: 2.5rem;
        }
      `}</style>
    </div>
  );
};

export default Appointment;